import * as THREE from 'three';
import { G, scene } from './scene';
/* ============================================================
   機體特效：噴焰 / 槍口閃光 / 排氣煙（1:1 移植 `mech_sample_ani.html` §11）
   ============================================================

   放在 render/ 是因為它要 `scene`。`entities/mech-anim.ts` 每幀呼叫
   `updateMechFX()`，所有狀態都由動畫層傳進來，這裡不自己判斷任何事。

   **這些物件一定要在 `mergeBones()` 之後才掛上去** —— 它們每幀改
   scale 與 opacity，被烘進靜態幾何就再也動不了，而且不會有任何錯誤。 */

function fxMat(hex, op) {
  return new THREE.MeshBasicMaterial({
    color: hex, transparent: true, opacity: op,
    blending: THREE.AdditiveBlending, depthWrite: false
  });
}

/* 排氣煙：30 顆物件池。加色 + `depthWrite:false`，速度衰減 + 微上浮 +
   二次方淡出。發射點用世界座標取樣，所以煙噴出去之後不跟著機體轉。 */
var POOL: any[] = [];
function initPool() {
  if (POOL.length) return;
  for (var i = 0; i < 30; i++) {
    var m = new THREE.Mesh(G.oct, fxMat(0x9fb4cc, 0));
    m.visible = false; scene.add(m);
    POOL.push({ m: m, life: 0, ttl: 1, v: new THREE.Vector3(), s0: 0.1, s1: 1 });
  }
}
function spawnPuff(pos, vel, s0, s1, ttl, hex) {
  for (var i = 0; i < POOL.length; i++) {
    var p = POOL[i];
    if (p.life > 0) continue;
    p.life = ttl; p.ttl = ttl; p.v.copy(vel); p.s0 = s0; p.s1 = s1;
    p.m.position.copy(pos); p.m.visible = true;
    p.m.material.color.setHex(hex);
    return;
  }
}
export function updatePuffs(dt) {
  for (var i = 0; i < POOL.length; i++) {
    var p = POOL[i];
    if (p.life <= 0) continue;
    p.life -= dt;
    if (p.life <= 0) { p.m.visible = false; continue; }
    var k = 1 - p.life / p.ttl;
    p.v.multiplyScalar(1 - 2.4 * dt); p.v.y += 0.85 * dt;
    p.m.position.addScaledVector(p.v, dt);
    var sc = p.s0 + (p.s1 - p.s0) * k;
    p.m.scale.set(sc, sc, sc);
    p.m.material.opacity = 0.5 * (1 - k) * (1 - k);
  }
}

var _wp = new THREE.Vector3();
/** 背部散熱口噴煙。`strength` 0..1.2，大招與排氣各給不同值。 */
export function puffAtVents(rig, strength) {
  if (!rig || !rig.vents) return;
  rig.back.updateMatrixWorld(true);
  for (var i = 0; i < rig.vents.length; i++) {
    var v = rig.vents[i];
    _wp.set(v[0], v[1], v[2]).applyMatrix4(rig.back.matrixWorld);
    spawnPuff(_wp.clone(),
      new THREE.Vector3((i ? -1 : 1) * 1.7 * strength, 0.5 * strength, -0.5 * strength),
      0.10, 0.42 + 0.5 * strength, 0.65 + 0.45 * strength, 0xbfd0e4);
  }
}
/** 槍口噴煙。 */
export function puffAtMuzzle(rig, big) {
  if (!rig || !rig.arm) return;
  rig.arm.aim.updateMatrixWorld(true);
  _wp.set(0, rig.muzzleY, 0).applyMatrix4(rig.arm.aim.matrixWorld);
  spawnPuff(_wp.clone(), new THREE.Vector3(0, 0.5, 0),
    big ? 0.3 : 0.12, big ? 1.3 : 0.45, big ? 0.75 : 0.4, 0xffd9a0);
}

/** 建立這台機體的噴焰與槍口閃光。**必須在 `mergeBones()` 之後呼叫。** */
export function buildMechFX(rig) {
  initPool();
  clearMechFX(rig);
  var fx: any = { flames: [], flash: null };
  rig.nozzles.forEach(function (n) {
    var grp = new THREE.Group();
    grp.position.set(n[0], n[1], n[2]);
    grp.rotation.x = -Math.PI / 2 - n[4];      // 錐尖朝後下
    var m = new THREE.Mesh(G.cone, fxMat(0xffc06a, 0.8));
    m.position.y = 0.5;                        // 錐底對齊噴口，縮放群組時底不會跑
    grp.add(m); (grp.userData as any).w = n[3];
    rig.back.add(grp); fx.flames.push(grp);
  });
  var f = new THREE.Mesh(G.oct, fxMat(0xfff0c0, 1));
  f.position.y = rig.muzzleY; f.visible = false;
  rig.arm.aim.add(f); fx.flash = f;
  rig.fx = fx;
  return fx;
}
export function clearMechFX(rig) {
  var fx = rig && rig.fx; if (!fx) return;
  fx.flames.forEach(function (g) { if (g.parent) g.parent.remove(g); });
  fx.flames.length = 0;
  if (fx.flash && fx.flash.parent) fx.flash.parent.remove(fx.flash);
  fx.flash = null;
  rig.fx = null;
}

/** 每幀更新。`thrust` 0..1 噴焰強度、`flash` 0..1 槍口閃光殘量。 */
export function updateMechFX(rig, t, thrust, flash) {
  var fx = rig && rig.fx; if (!fx) return;
  for (var i = 0; i < fx.flames.length; i++) {
    var fl = fx.flames[i], w = (fl.userData as any).w;
    var th = thrust * (0.86 + 0.28 * Math.sin(t * 33 + i * 2.1));
    fl.visible = th > 0.02;
    fl.scale.set(w * (0.62 + 0.5 * th), (0.22 + 1.9 * th) * (w / 0.30), w * (0.62 + 0.5 * th));
    fl.children[0].material.opacity = Math.min(0.95, Math.max(0, th * 1.15));
  }
  if (fx.flash) {
    fx.flash.visible = flash > 0.02;
    fx.flash.scale.set(0.5 * flash, 1.5 * flash, 0.5 * flash);
    fx.flash.material.opacity = flash;
  }
}
